import { useCallback, useState } from "react";
import { fetchAuthSession, fetchUserAttributes, signIn } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import ls from "@/utils/SecureLS";
import { useAppDispatch } from "@/store/hooks";
import { setDistributorInfo, setStylistInfo, setUser } from "@/store/slices/authSlice";
import { executeGraphQLMutation } from "@/utils/helper/graphql-helper";
import { listSellerInfos, listStylistInfos, listUserInfos } from "@/graphql/queries";
import useSignOut from "./useSignOut";

const useSignInUser = () => {
  const [loading, setLoading] = useState(false);

  const {handleSignOut} = useSignOut(false);

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const getUserGroups = async () => {
    try {
      const { accessToken } = (await fetchAuthSession()).tokens ?? {};
      const groups = accessToken?.payload["cognito:groups"] ?? [];
      return groups;
    } catch (err) {
      console.error("fetchAuthSession:: ", err);
      return [];
    }
  };

  const fetchAndStoreUser = async () => {
    try {
      const userAttributes = await fetchUserAttributes();

      if (userAttributes) {
        const userInfos = await executeGraphQLMutation({
          query: listUserInfos,
          variables: {
            filter: {
              user_id: { eq: userAttributes?.sub || "" },
            },
          },
        });

        if (userInfos?.items?.length === 0) {
          toast.error("User not found.", {
            description: "You may have not completed the signup process.",
          });
        }

        const userInfo = userInfos?.items?.[0];
      

        if (userInfo) {
          if (userInfo?.current_role === "seller") {
         
            const distributorInfo = await executeGraphQLMutation({
              query: listSellerInfos,
              variables: {
                filter: {
                  user_id: {
                    eq: userInfo?.user_id,
                  },
                },
              },

              setLoading,
            });

            if (distributorInfo?.items?.length > 0 && !distributorInfo?.items?.[0]?.is_verified) {

              toast.error("You account is not verified yet by the admin.", {
                description: "Please check back later.",
              });
              await  handleSignOut();
              return;
            }

            dispatch(setDistributorInfo(distributorInfo?.items[0]));
            ls.set("distributorInfo", JSON.stringify(distributorInfo?.items[0]));

            await setSessionAndUser(userInfo);

            navigate("/seller", {
              state: {
                name: userAttributes?.name || "",
              },
            });
          } else if (userInfo?.current_role === "buyer") {
            const stylistInfo = await executeGraphQLMutation({
              query: listStylistInfos,
              variables: {
                filter: {
                  user_id: {
                    eq: userInfo?.user_id,
                  },
                },
              },

              setLoading,
            });

            if (stylistInfo?.items?.length > 0 && !stylistInfo?.items?.[0]?.is_verified) {
              toast.error("You account is not verified yet by the admin.", {
                description: "Please check back later.",
              });
              await  handleSignOut();
              return;
            }

            dispatch(setStylistInfo(stylistInfo?.items[0]));
            ls.set("stylistInfo", JSON.stringify(stylistInfo?.items[0]));

            await setSessionAndUser(userInfo);

            toast.success("Login successful", {
              description: `Welcome back! ${userAttributes?.name}`,
            });
            window.location.reload();
          } else if (userInfo?.current_role === "super-admin") {
            await setSessionAndUser(userInfo);
            navigate("/super-admin");
          } else {
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error("fetchAndStoreUser:: ", error);
    }
  };

  const handleSignIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);

      const user = await signIn({ username: email, password });

      if (user?.isSignedIn) {
        await fetchAndStoreUser();
      }

      return user;
    } catch (error: any) {
      if (error?.name === "UserNotFoundException") {
        toast.error("User with this email does not exist.", {
          description: "Please Sign Up first.",
        });
      } else if (error?.name === "NotAuthorizedException") {
        toast.error("Incorrect password. Please try again.", {
          description: "Make sure your password is correct.",
        });
      } else {
        console.error("handleSignIn:: ", error);
        toast.error("", {
          description: error?.message || "An error occurred. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const setSessionAndUser = async (userInfo: any) => {
    dispatch(setUser(userInfo));
    ls.set("user", JSON.stringify(userInfo));
    sessionStorage.setItem("isLoginSession", "true");

    await getUserGroups();
    sessionStorage.setItem("isLoginSession", "true");
  };

  return {
    handleSignIn,
    loading,
  };
};
export default useSignInUser;
